// Who's logged in, and keeping unauthenticated visitors out of private pages.
//
// This replaces `proxy.ts` + lib/supabase/middleware.ts. Two layers, and the
// order matters:
//
//   1. <ProtectedRoute> below — cosmetic. It stops a private page flashing
//      before the redirect, nothing more.
//   2. The API — the real gate. Every endpoint checks the caller
//      independently, so a client that skipped the router still gets nothing.
//
// That is not weaker than before: `proxy.ts` also only redirected — the actual
// enforcement was RLS in the database. Now it's the API's auth middleware.

import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import type { User } from "@historia/shared/contracts";
import { clearToken, setUnauthenticatedHandler } from "./client";
import { useMe } from "./queries";

interface SessionValue {
  user: User | null;
  loading: boolean;
}

const SessionContext = createContext<SessionValue>({ user: null, loading: true });

export function SessionProvider({ children }: { children: ReactNode }) {
  const { data, isPending, isError } = useMe();
  const queryClient = useQueryClient();

  // A 401 from any request EXCEPT the session probe means the session ended
  // mid-use: drop the token and the cached data so a stale page can't linger.
  //
  // `resetQueries` rather than `clear()`: clear() also wipes the `me` query,
  // which immediately refetches, 401s, and clears again. The session probe is
  // already excluded in client.ts, but resetting only what is actually stale
  // keeps that loop impossible from either direction.
  useEffect(() => {
    setUnauthenticatedHandler(() => {
      clearToken();
      void queryClient.resetQueries({
        predicate: (query) => query.queryKey[0] !== "me",
      });
    });
  }, [queryClient]);

  const value = useMemo<SessionValue>(
    () => ({
      user: isError ? null : data ?? null,
      // `isPending` alone is not enough: a query that errored is settled, but
      // React Query keeps isPending true when there is no data. Treating an
      // error as "finished, and the answer is no" is what lets ProtectedRoute
      // redirect instead of waiting forever.
      loading: isPending && !isError,
    }),
    [data, isPending, isError],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  return useContext(SessionContext);
}

/**
 * Wraps the private route subtree.
 *
 * While the session is resolving it renders nothing rather than the login page:
 * showing /login for a moment to someone who IS logged in is worse than a brief
 * blank, especially on a slow phone connection.
 */
export function ProtectedRoute() {
  const { user, loading } = useSession();
  const location = useLocation();

  if (loading) return <Splash />;

  if (!user) {
    // Remember where they were headed. `safeDestination` in shared/validation.ts
    // is what stops that value being used to bounce someone off-site.
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?seguinte=${next}`} replace />;
  }

  return <Outlet />;
}

/** Quiet placeholder while the session resolves. */
function Splash() {
  return (
    <div className="flex min-h-dvh items-center justify-center">
      <p className="animate-pulse font-display text-lg text-tinta-700">A abrir o nosso baú…</p>
    </div>
  );
}
